import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Center,
  Text, VStack, Image, HStack, AspectRatio, Flex,
} from '@chakra-ui/react';
import {
  FaCamera,
  FaRedo,
  FaTrash
} from 'react-icons/fa';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'utils/crop-image';
import Loading from '../Loading/Loading';
import { useTranslation } from "react-i18next";

const WebcamCapture = ({ onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const modalContentRef = useRef(null);
  const timeoutRef = useRef(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();

  const [isVideoReady, setIsVideoReady] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [finalImage, setFinalImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isLoadingCamera, setIsLoadingCamera] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      try {
        const tracks = streamRef.current.getTracks();
        tracks.forEach(track => {
          track.stop();
        });
        console.log(t("system.shared-components.webcam-capture.logs.camera-disabled-successfully"));
      } catch (err) {
        console.error(t("system.shared-components.webcam-capture.errors.track-stop-error"), err);
      }
      streamRef.current = null;
    }

    if (videoRef.current) {
      try {
        videoRef.current.srcObject = null;
      } catch (err) {
        console.error(t("system.shared-components.webcam-capture.errors.clear-src-error"), err);
      }
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const initializeCamera = useCallback(async () => {
    if (isLoadingCamera || isVideoReady) return;
    setIsLoadingCamera(true);
    setHasError(false);
    setErrorMessage('');
    stopCamera();

    timeoutRef.current = setTimeout(() => {
      if (isLoadingCamera && !isVideoReady) setLoadingTimeout(true);
    }, 10000);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      if (!videoRef.current || !isOpen) throw new Error(t("system.shared-components.webcam-capture.errors.video-initialization-error"));
      videoRef.current.srcObject = stream;
      videoRef.current.onloadeddata = () => {
        setIsVideoReady(true);
        setIsLoadingCamera(false);
        setLoadingTimeout(false);
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      };
      videoRef.current.onerror = e => {
        setHasError(true);
        setErrorMessage(t("system.shared-components.webcam-capture.errors.video-initialization-error"));
        setIsLoadingCamera(false);
      };
      try {
        await videoRef.current.play();
      } catch (playError) {
        setHasError(true);
        setErrorMessage(t("system.shared-components.webcam-capture.errors.video-play-error"));
        setIsLoadingCamera(false);
      }
    } catch (err) {
      setHasError(true);
      setErrorMessage(err.message || t("system.shared-components.webcam-capture.errors.access-error"));
      stopCamera();
      setIsLoadingCamera(false);
    }
  }, [isLoadingCamera, isVideoReady, stopCamera, isOpen]);

  const handleCloseModal = useCallback(() => {
    stopCamera();
    setIsVideoReady(false);
    setCapturedImage(null);
    setLoadingTimeout(false);
    onClose();
  }, [stopCamera, onClose]);

  useEffect(() => {
    if (isOpen && !isVideoReady && !capturedImage) {
      setTimeout(initializeCamera, 100);
    }
    return () => { if (!isOpen) stopCamera(); };
  }, [isOpen, isVideoReady, capturedImage, initializeCamera, stopCamera]);

  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      setIsVideoReady(false);
      setIsLoadingCamera(false);
      setCapturedImage(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setLoadingTimeout(false);
    }
  }, [isOpen, stopCamera]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setCapturedImage(canvas.toDataURL('image/png'));
    stopCamera();
    setIsVideoReady(false);
  };

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const showCroppedImage = async () => {
    try {
      const croppedImg = await getCroppedImg(capturedImage, croppedAreaPixels);
      setFinalImage(croppedImg);
      onCapture(croppedImg);
      handleCloseModal();
    } catch (err) {
      console.error(t("system.shared-components.webcam-capture.errors.crop-image-error"), err);
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setIsVideoReady(false);
    setTimeout(initializeCamera, 300);
  };

  const handleRetry = () => {
    setHasError(false);
    setErrorMessage('');
    setLoadingTimeout(false);
    setIsLoadingCamera(false);
    setTimeout(initializeCamera, 300);
  };

  const handleClearImage = () => {
    setFinalImage(null);
  };

  return (
    <VStack
      spacing={4}
      align="left"
    >

      {finalImage && (
        <AspectRatio
          ratio={3 / 4}
          maxW="120px"
        >
          <Image
            src={finalImage}
            objectFit="cover"
            borderRadius="md"
          />
        </AspectRatio>
      )}

      <HStack spacing={2}>
        <Button
          fontSize="sm"
          leftIcon={<FaCamera />}
          px="10"
          variant="dark"
          width="150px"
          onClick={onOpen}
        >
          {finalImage ?
            t("system.shared-components.webcam-capture.actions.change-picture") :
            t("system.shared-components.webcam-capture.actions.capture-picture")
          }
        </Button>
        {finalImage && (
          <Button
            fontSize="sm"
            leftIcon={<FaTrash />}
            variant="outline"
            colorScheme="light"
            px="10"
            width="150px"
            onClick={handleClearImage}
          >
            {t("system.actions.clear")}
          </Button>
        )}
      </HStack>

      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        size="xl"
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent ref={modalContentRef}>
          <ModalHeader>{t("system.shared-components.webcam-capture.title")}</ModalHeader>
          <ModalBody>
            {isLoadingCamera && !isVideoReady && !hasError && (
              <Center
                w="100%"
                h="300px"
                flexDirection="column"
              >
                <Loading />
                {loadingTimeout && (
                  <Box mt={4}>
                    <Text color="orange.500" mb={2}>
                      {t("system.shared-components.webcam-capture.messages.camera-in-use")}
                    </Text>
                    <Button
                      colorScheme="orange"
                      size="sm"
                      leftIcon={<FaRedo />}
                      onClick={handleRetry}
                    >
                      {t("system.shared-components.webcam-capture.actions.retry")}
                    </Button>
                  </Box>
                )}
              </Center>
            )}
            {hasError && (
              <Center w="100%" h="300px" flexDirection="column">
                <Box
                  mb={4}
                  textAlign="center"
                  color="red.500"
                >
                  {errorMessage}
                </Box>
                <Button
                  colorScheme="red"
                  fontSize="sm"
                  leftIcon={<FaRedo />}
                  onClick={handleRetry}
                >
                  {t("system.shared-components.webcam-capture.actions.retry")}
                </Button>
              </Center>
            )}
            <Box
              w="100%"
              h="400px"
              display={(isVideoReady && !capturedImage) ? 'block' : 'none'}
              position="relative"
              borderRadius="8px"
              overflow="hidden"
              bg="black"
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
            {capturedImage && (
              <Box
                position="relative"
                width="100%"
                height="400px"
              >
                <Cropper
                  image={capturedImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={3 / 4}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </Box>
            )}
            <canvas
              ref={canvasRef}
              style={{ display: 'none' }}
            />
          </ModalBody>
          <ModalFooter>
            {!capturedImage ? (
              <Flex
                w="100%"
                align="center"
                justify="space-between"
              >
                <Button
                  fontSize="sm"
                  onClick={handleCloseModal}
                  colorScheme="gray"
                  px="10"
                  width="150px"
                >
                  {t("system.actions.cancel")}
                </Button>
                <Button
                  fontSize="sm"
                  leftIcon={<FaCamera />}
                  px="10"
                  variant="dark"
                  width="150px"
                  onClick={handleCapture}
                  isDisabled={!isVideoReady || isLoadingCamera || hasError}
                >
                  {t("system.shared-components.webcam-capture.actions.capture")}
                </Button>
              </Flex>
            ) : (
              <Flex
                w="100%"
                gap="4"
                align="center"
                justify="space-between"
              >
                <Button
                  fontSize="sm"
                  leftIcon={<FaRedo />}
                  colorScheme="gray"
                  px="10"
                  width="150px"
                  onClick={handleRetake}
                >
                  {t("system.shared-components.webcam-capture.actions.redo")}
                </Button>
                <Button
                  fontSize="sm"
                  leftIcon={<FaCamera />}
                  px="10"
                  variant="dark"
                  width="150px"
                  onClick={showCroppedImage}
                >
                  {t("system.shared-components.webcam-capture.actions.use-picture")}
                </Button>
              </Flex>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default WebcamCapture;
